import axios from "axios";
import { isTelegramWebApp } from "../utils/telegramDetect";

// hrSystemServer API base URL for Telegram authentication
const HR_API_BASE_URL = "https://api.oneplace.hr";

// aichatbot API base URL for Telegram Web App authentication
const AICHATBOT_API_URL = "https://aichatbot-326159028339.asia-southeast1.run.app";

const getChatSourceChannel = () => isTelegramWebApp() ? "telegram" : "career_page";

/**
 * Authentication API service for Telegram OAuth
 */
const authApi = {
  // ===== TELEGRAM WEB APP AUTHENTICATION (Mini App - instant login) =====

  /**
   * Verify Telegram Web App initData and get JWT token
   * Used when app is opened inside Telegram Mini App
   * @param {Object} data - Web App verification data
   * @param {string} data.initData - Raw initData string from Telegram.WebApp.initData
   * @param {string} data.country - Country code (MN, UZ, KZ)
   * @returns {Promise<Object>} { token, candidateId, phone, firstName, needsPhone }
   */
  verifyTelegramWebApp: async (data) => {
    try {
      const response = await axios.post(`${AICHATBOT_API_URL}/user/telegram/webapp/verify`, data);
      return response.data;
    } catch (error) {
      console.error("Error verifying Telegram Web App:", error);
      throw error;
    }
  },

  /**
   * Link phone number to Telegram account
   * Called after requestContact() when user is new (needsPhone: true)
   * @param {string} phone - Phone number from Telegram contact share
   * @param {string} token - JWT token from verifyTelegramWebApp
   * @returns {Promise<Object>} { success, merged, token, candidateId, phone }
   */
  linkTelegramPhone: async (phone, token) => {
    try {
      const response = await axios.post(
        `${AICHATBOT_API_URL}/user/telegram/link-phone`,
        { phone },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      console.error("Error linking Telegram phone:", error);
      throw error;
    }
  },

  // ===== BOT-BASED LOGIN (new - with phone collection) =====

  /**
   * Initialize a Telegram bot login session.
   * Returns a deep link for user to open the bot.
   * @param {string} country - Country code (MN, UZ, KZ)
   * @returns {Promise<Object>} { loginToken, deepLink, expiresInSeconds }
   */
  initTelegramBotSession: async (country) => {
    try {
      const response = await axios.post(`${HR_API_BASE_URL}/v1/auth/telegram/bot/init-session`, {
        country,
      });
      return response.data;
    } catch (error) {
      console.error("Error initializing Telegram bot session:", error);
      throw error;
    }
  },

  /**
   * Check the status of a Telegram bot login session.
   * @param {string} loginToken - The session token from init-session
   * @returns {Promise<Object>} { status, token?, candidateId?, phone?, firstName?, lastName? }
   */
  checkTelegramBotStatus: async (loginToken) => {
    try {
      const response = await axios.get(`${HR_API_BASE_URL}/v1/auth/telegram/bot/check-status/${loginToken}`);
      return response.data;
    } catch (error) {
      console.error("Error checking Telegram bot status:", error);
      throw error;
    }
  },

  // ===== WIDGET-BASED LOGIN (existing - no phone) =====

  /**
   * Verify Telegram authentication data and get JWT token
   * @param {Object} telegramData - Data from Telegram OAuth callback
   * @param {number} telegramData.id - Telegram user ID
   * @param {string} telegramData.first_name - User's first name
   * @param {string} telegramData.last_name - User's last name (optional)
   * @param {string} telegramData.username - Telegram username (optional)
   * @param {string} telegramData.photo_url - Profile photo URL (optional)
   * @param {number} telegramData.auth_date - Unix timestamp of authentication
   * @param {string} telegramData.hash - Verification hash from Telegram
   * @param {string} country - Country code (MN, UZ, KZ)
   * @returns {Promise<Object>} Response with JWT token and candidate info
   */
  verifyTelegram: async (telegramData, country) => {
    try {
      const response = await axios.post(`${HR_API_BASE_URL}/v1/auth/telegram/verify`, {
        ...telegramData,
        country,
      });
      return response.data;
    } catch (error) {
      console.error("Error verifying Telegram auth:", error);
      throw error;
    }
  },

  /**
   * Update candidate profile after Telegram login
   * @param {Object} profileData - Profile data to update
   * @param {number} profileData.candidateId - Candidate ID
   * @param {string} profileData.phone - Phone number (optional)
   * @param {string} profileData.firstName - First name (optional)
   * @param {string} profileData.lastName - Last name (optional)
   * @param {string} profileData.birthdate - Birthdate YYYY-MM-DD (optional)
   * @returns {Promise<Object>} Updated candidate info
   */
  updateProfile: async (profileData) => {
    try {
      const response = await axios.post(`${HR_API_BASE_URL}/v1/auth/telegram/update-profile`, profileData);
      return response.data;
    } catch (error) {
      console.error("Error updating profile:", error);
      throw error;
    }
  },

  // ===== PHONE OTP AUTHENTICATION =====

  /**
   * Send OTP to phone number
   * @param {string} phone - Phone number (local format)
   * @param {string} country - Country code (MN, UZ, KZ)
   * @returns {Promise<Object>} { success, message, expiresInSeconds }
   */
  sendOtp: async (phone, country = "MN") => {
    try {
      const response = await axios.post(`${HR_API_BASE_URL}/v1/auth/candidate/otp/send`, {
        phone,
        country,
      });
      return response.data;
    } catch (error) {
      console.error("Error sending OTP:", error);
      throw error;
    }
  },

  /**
   * Verify OTP and get JWT token
   * @param {string} phone - Phone number (local format)
   * @param {string} code - 6-digit OTP code
   * @param {string} country - Country code (MN, UZ, KZ)
   * @returns {Promise<Object>} { token, candidateId, phone, firstName, lastName, isNewUser }
   */
  verifyOtp: async (phone, code, country = "MN") => {
    try {
      const response = await axios.post(`${HR_API_BASE_URL}/v1/auth/candidate/otp/verify`, {
        phone,
        code,
        country,
        source: "CHAT",
        sourceChannel: getChatSourceChannel(),
      });
      return response.data;
    } catch (error) {
      console.error("Error verifying OTP:", error);
      throw error;
    }
  },
};

export default authApi;
